import { cast, Instance, types, flow, getEnv } from "mobx-state-tree";
import { GridSortItem, GridFilterModel, GridColumnVisibilityModel } from "@mui/x-data-grid";

import { Column, ColumnType, Option, SortDirection } from "./interfaces";
import serializeToQueryParams from "../../helpers/QueryParams";
import getSortUrlParams from "./helpers/sortExtensions";
import { StorageModel } from "../../services/storage/StorageModel";
import { HeaderMetadata } from "../../api-models/response/DataGridResponse";
import { PropertyType } from "../../generated/SewerObjects";

const SortColumnModel = types.model({
  columnKey: types.optional(types.string, ""),
  direction: types.enumeration<SortDirection>(Object.values(SortDirection)),
});

const FilterModel = types.model({
  gridFilter: types.frozen<GridFilterModel | undefined>(),
  contextFilter: types.frozen(),
});

const DataGridModel = types.model({
  start: 0,
  end: 20,
  selectedItems: types.optional(types.array(types.frozen()), []),
  sortedColumn: types.maybeNull(
    types.optional(SortColumnModel, { direction: SortDirection.NotSorted })
  ),
  filterData: types.optional(FilterModel, {}),
  defaultSort: types.optional(types.frozen<GridSortItem | undefined>(), undefined),
  defaultFilter: types.optional(types.frozen<GridFilterModel | undefined>(), undefined),
  defaultColumnVisibility: types.optional(
    types.frozen<GridColumnVisibilityModel | undefined>(),
    undefined
  ),
  shouldResetPagination: types.optional(types.boolean, false),
  shouldResetSelection: types.optional(types.boolean, false),
  clientSidePagination: types.optional(types.boolean, false),
  noPagination: types.optional(types.boolean, false),
  useGridFiltering: types.optional(types.boolean, false),
  gridColumns: types.maybe(types.frozen<Column[]>()),
  isCollapsed: types.optional(types.boolean, false),
});

export const DataGridStorageModel = types
  .compose(DataGridModel, StorageModel)
  .named("DataGridStorageModel")
  .views(() => ({
    get endpointOnlyIds() {
      return {};
    },
  }))
  .actions((self) => {
    return {
      mapItem(item: any) {
        return item;
      },
      handleCollapse(isCollapsed: boolean) {
        self.isCollapsed = !isCollapsed;
      },
    };
  })
  .actions((self) => {
    const removeAllFromSelection = () => {
      while (self.selectedItems.length > 0) {
        self.selectedItems.pop();
      }
    };

    const loadWithPaginationReset = async () => {
      const resultsPerPage = self.end - self.start;
      self.start = 0;
      self.end = resultsPerPage;
      await self.load();
    };

    return {
      mapResponse(response: any) {
        if (self.clientSidePagination) self.data = response.map(self.mapItem);
        else
          self.data = {
            totalCount: response.totalCount,
            items: response.results.map(self.mapItem),
          };
      },
      async sortItemsBy(column: GridSortItem) {
        self.sortedColumn = SortColumnModel.create({
          columnKey: column.field,
          direction: column.sort === "asc" ? SortDirection.Asc : SortDirection.Desc,
        });
      },
      setSelectedItems(selectedItems: string[]) {
        self.selectedItems = cast(selectedItems);
      },
      deselectAll() {
        self.shouldResetSelection = true;
        removeAllFromSelection();
      },
      async loadItemsSet(start: number, end: number) {
        if (self.start !== start || self.end !== end) {
          self.start = start;
          self.end = end;
          await self.load();
        }
      },
      async setContextFilter(contextFilter: any, shouldResetPagination: boolean = false) {
        self.filterData.contextFilter = contextFilter;
        self.shouldResetPagination = shouldResetPagination;
        self.shouldResetSelection = true;

        await (shouldResetPagination ? loadWithPaginationReset() : self.load());
      },
      async setGridFilter(gridFilter: GridFilterModel, shouldResetPagination: boolean) {
        self.filterData.gridFilter = gridFilter;
        self.shouldResetPagination = shouldResetPagination;
      },
      async setGridFilterAndLoad(gridFilter: GridFilterModel, shouldResetPagination: boolean) {
        self.filterData.gridFilter = gridFilter;
        self.shouldResetPagination = shouldResetPagination;
        self.shouldResetSelection = true;

        await (shouldResetPagination ? loadWithPaginationReset() : self.load());
      },
      resetGridFilter() {
        self.filterData.gridFilter = undefined;
        removeAllFromSelection();
      },
      resetPaginationState() {
        self.shouldResetPagination = false;
      },
      resetSelectionState() {
        self.shouldResetSelection = false;
      },
      resetDataQuery() {
        self.start = 0;
        self.end = 20;
        self.sortedColumn = null;
      },
      loadOnlyIds: flow(function* (): Generator<any, number[], any> {
        const response = yield getEnv(self).api.post(self.endpointOnlyIds, self.requestBody);
        return response.data?.map((id: any) => id) ?? [];
      }),
      setDefaultVisibilityModel(newVisibilityModel: GridColumnVisibilityModel) {
        self.defaultColumnVisibility = newVisibilityModel;
      },
    };
  })
  .views((self) => ({
    getSortQuery() {
      const sortedColumn = self.sortedColumn;
      if (sortedColumn) {
        const sortUrlParams = getSortUrlParams(sortedColumn.columnKey, sortedColumn.direction);
        return sortUrlParams;
      }
      return null;
    },
  }))
  .views((self) => ({
    get columns() {
      return self.gridColumns ?? [];
    },
    get rows() {
      if (self.clientSidePagination) return self.data ?? [];
      return self.data?.items ?? [];
    },
    get anyItemsSelected() {
      return self.selectedItems.length > 0;
    },
    get moreThanOneItemsSelected() {
      return self.selectedItems.length > 1;
    },
    get selectedItemsCount() {
      return self.selectedItems.length;
    },
    get allItemsCount() {
      if (self.data) return self.data.totalCount;

      return 0;
    },
    get hasAvailableItems() {
      return self.data && self.data.totalCount > 0;
    },
    get hasItems() {
      if (self.clientSidePagination) return !!self.data?.length;
      return !!self.data?.items.length;
    },
    getColumnType(metadata: HeaderMetadata): ColumnType {
      if (metadata.predefinedOptions?.length > 0) return ColumnType.SingleSelect;
      if (metadata.type === PropertyType.String) return ColumnType.String;
      if (
        metadata.type === PropertyType.Decimal ||
        metadata.type === PropertyType.Integer ||
        metadata.type === PropertyType.Double
      )
        return ColumnType.Number;
      if (metadata.type === PropertyType.Date) return ColumnType.DateTime;

      return ColumnType.String;
    },
    getColumnPredefinedValueOptions(metadata: HeaderMetadata): Option[] | undefined {
      if (!metadata.predefinedOptions) return undefined;

      return metadata.predefinedOptions.map((item) => {
        return {
          label: item.value,
          value: item.key,
        };
      });
    },
    getDataQuery() {
      const start = self.start;
      const end = self.end;
      const sortedColumn = self.getSortQuery();

      return sortedColumn
        ? serializeToQueryParams({ start, end, ...self.filterData.contextFilter, ...sortedColumn })
        : serializeToQueryParams({ start, end, ...self.filterData.contextFilter });
    },
    get requestBody() {
      return self.useGridFiltering && !self.clientSidePagination
        ? self.filterData?.gridFilter ?? {}
        : null;
    },
    get hasFilterApplied() {
      return (
        self.filterData.gridFilter?.items?.length &&
        self.filterData.gridFilter.items.every((i) => i.value)
      );
    },
  }));

export type DataGridStorage = Instance<typeof DataGridStorageModel>;
